import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Undo2, Wand2 } from "lucide-react";
import { aiPosterPreview, aiRewrite, getSetupStatus, LOGO_URL } from "../lib/api";
import { LANGUAGES, PLATFORMS, REWRITE_LABELS, platformLabel } from "../lib/platforms";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

// Company name and logo, for the preview cards.
export function useBrand() {
  const [brand, setBrand] = useState(null);
  useEffect(() => { getSetupStatus().then(setBrand).catch(() => {}); }, []);
  return brand;
}

// Characters used against the platform's limit.
export function CharMeter({ text, platform }) {
  const limit = PLATFORMS[platform]?.limit || 0;
  const n = text.length;
  const pct = limit ? Math.min(100, Math.round((n / limit) * 100)) : 0;
  const tone = limit && n > limit ? "over" : pct > 90 ? "near" : "ok";
  return <div className={`char-meter ${tone}`} title={limit ? `${platformLabel(platform)} allows ${limit.toLocaleString()} characters` : undefined}>
    <span style={{ width: `${pct}%` }} /><small>{n.toLocaleString()}{limit ? ` / ${limit.toLocaleString()}` : ""}</small>
  </div>;
}

// The poster as it will be made, from the headline. Updates a moment after typing stops.
export function PosterPreview({ headline, sub, tag }) {
  const [png, setPng] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    if (!headline?.trim()) { setPng(null); return undefined; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { setPng((await aiPosterPreview({ headline, sub, tag })).png_base64); } catch { setPng(null); }
      finally { setBusy(false); }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [headline, sub, tag]);
  return <div className="poster-preview" data-testid="poster-preview">
    {png ? <img src={`data:image/png;base64,${png}`} alt="Poster preview" /> : <div className="poster-empty">{busy ? <Loader2 size={18} className="spin" /> : "Poster preview"}</div>}
    {png && busy && <div className="poster-busy"><Loader2 size={16} className="spin" /></div>}
  </div>;
}

// A realistic card of the post, the way the platform shows it.
export function PostPreview({ platform, text, poster }) {
  const brand = useBrand();
  const p = PLATFORMS[platform] || PLATFORMS.linkedin;
  const name = brand?.company_name || "Your company";
  const cut = platform === "instagram" ? 125 : platform === "linkedin" ? 210 : 280;
  const long = text.length > cut;
  return <div className={`post-card post-card-${platform}`} data-testid="post-preview">
    <div className="post-card-head">
      <span className="post-avatar" style={{ background: brand?.has_logo ? "#fff" : p.color }}>
        {brand?.has_logo ? <img src={`${LOGO_URL}?v=${brand.logo_v || 0}`} alt="" /> : name.slice(0, 1).toUpperCase()}</span>
      <div><b>{name}</b><small>{platformLabel(platform)} · Just now</small></div>
    </div>
    {platform === "instagram" && poster}
    <p className="post-card-text">{long ? text.slice(0, cut) : text || "Your post appears here as you write it."}{long && <span className="post-more">… more</span>}</p>
    {platform !== "instagram" && poster}
    <div className="post-card-foot"><span>Like</span><span>Comment</span><span>Share</span></div>
  </div>;
}

// One-click edits of a draft: shorter, punchier, translate… The result replaces the text; Undo brings it back.
export function RewriteBar({ text, platform, model, onResult }) {
  const [busy, setBusy] = useState("");
  const [previous, setPrevious] = useState(null);
  const [language, setLanguage] = useState("");
  const run = async (action, lang) => {
    setBusy(action);
    try {
      const r = await aiRewrite({ text, action, platform, language: lang, model: model?.provider === "auto" ? undefined : model });
      setPrevious(text); onResult(r.text, r);
      if (r.unverified_numbers?.length) toast.warning(`Check these numbers: ${r.unverified_numbers.join(", ")}`);
    } catch (e) { toast.error(errText(e, "The AI could not change that")); }
    finally { setBusy(""); }
  };
  return <div className="rewrite-bar" data-testid="rewrite-bar">
    <span className="rewrite-label"><Wand2 size={12} /> Improve</span>
    {Object.entries(REWRITE_LABELS).map(([k, label]) => <button key={k} className="pill" disabled={!!busy || !text.trim()} onClick={() => run(k)} data-testid={`rewrite-${k}`}>
      {busy === k ? <Loader2 size={11} className="spin" /> : null}{label}</button>)}
    <select className="pill pill-select" value={language} disabled={!!busy || !text.trim()} data-testid="rewrite-translate"
      onChange={e => { const l = e.target.value; setLanguage(""); if (l) run("translate", l); }}>
      <option value="">Translate…</option>{LANGUAGES.slice(1).map(l => <option key={l}>{l}</option>)}
    </select>
    {previous !== null && <button className="pill pill-undo" onClick={() => { onResult(previous, null); setPrevious(null); }} data-testid="rewrite-undo"><Undo2 size={11} /> Undo</button>}
  </div>;
}
